import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTemplateFolders } from "@/queries/forms/template-folder.query.ts";
import { ApiPermission } from "@/types/role.types.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { Button, FullScreenPage, Input, Stack, Select } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  usePerformanceReviewTemplateSave,
  usePerformanceReviewTemplateMove,
} from "@/queries/performance-reviews/use-performance-review-template.query.ts";
import {
  PerformanceReviewTemplateEditPageStep,
  TemplateEditPageProps,
  useTemplateEditPage,
} from "@/pages/talent/performance-reviews/templates/edit/performance-review-template-edit.tsx";
import { PerformanceReviewTemplateTitleSlot } from "@/pages/talent/performance-reviews/templates/edit/PerformanceReviewTemplateTitleSlot.tsx";
import { PerformanceReviewTemplateEditSidebar } from "@/pages/talent/performance-reviews/templates/edit/components/PerformanceReviewTemplateEditSidebar.tsx";

export const PerformanceReviewTemplateEditDetailsPage = ({ template }: TemplateEditPageProps) => {
  const { t } = useTranslation();
  const { exit, confirmExit, navigateToStep } = useTemplateEditPage();
  const { canIEdit } = usePermissions();
  const readOnly = !canIEdit(ApiPermission.performance_review_templates);

  const {
    data: folders,
    isLoading: foldersLoading,
    isError: foldersError,
  } = useTemplateFolders("performance-reviews");
  const templateSchema = z.object({
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    folder_id: z
      .number({ error: t("errors:field_required") })
      .refine((id) => folders?.some((folder) => folder.id === id), t("errors:field_required")),
  });

  type TemplateSchema = z.infer<typeof templateSchema>;

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<TemplateSchema>({
    mode: "onSubmit",
    resolver: zodResolver(templateSchema),
    defaultValues: {
      title: template.title,
      folder_id: template.folder?.id,
    },
  });

  const templateSave = usePerformanceReviewTemplateSave();
  const templateMove = usePerformanceReviewTemplateMove();
  const isSaving = templateSave.isPending || templateMove.isPending;
  const onSubmit = handleSubmit(async (data) => {
    try {
      await templateSave.mutateAsync({
        id: template.id,
        payload: { title: data.title },
      });

      if (data.folder_id !== template.folder?.id) {
        await templateMove.mutateAsync({ id: template.id, payload: { folder_id: data.folder_id } });
      }

      analyticsService.trackEvent(analyticEvents.performanceReviewsTemplates.edited, {
        [analyticProperties.id]: template.id,
        [analyticProperties.tab]: PerformanceReviewTemplateEditPageStep.details,
      });

      navigateToStep(template.id, PerformanceReviewTemplateEditPageStep.questions);
    } catch {
      return;
    }
  });

  const onContinue = readOnly
    ? () => navigateToStep(template.id, PerformanceReviewTemplateEditPageStep.questions)
    : onSubmit;
  const continueDisabled = !readOnly && (foldersLoading || foldersError);

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={readOnly ? exit : confirmExit}>
          {t("common:exit")}
        </Button>
      }
      titleSlot={
        <PerformanceReviewTemplateTitleSlot step={PerformanceReviewTemplateEditPageStep.details} />
      }
      isCenterVertical={false}
      contentSize={"md"}
      sidebarSlot={
        <PerformanceReviewTemplateEditSidebar
          step={PerformanceReviewTemplateEditPageStep.details}
          template={template}
        />
      }
      actionsSlot={
        <Button onClick={onContinue} size={"lg"} isLoading={isSaving} disabled={continueDisabled}>
          {t("common:continue")}
        </Button>
      }
    >
      <Stack gap={"sm"} justify={"center"}>
        <Stack className={"card-panel"}>
          <Input
            type={"text"}
            label={t("common:title")}
            size={"lg"}
            {...register("title")}
            disabled={readOnly}
            error={errors.title?.message}
          />
          <Controller
            control={control}
            name="folder_id"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("core:folder")}
                emptyTitle={t("common:select")}
                options={folders}
                value={value}
                onChange={onChange}
                isLoading={foldersLoading}
                disabled={isSaving || readOnly}
                required
                error={errors.folder_id?.message}
              />
            )}
          />
        </Stack>
        <Button onClick={onContinue} size={"lg"} isLoading={isSaving} disabled={continueDisabled}>
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
};
